import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Security as SecurityIcon,
  RadioButtonUnchecked as AmberIcon
} from '@mui/icons-material';
import { useTenant } from '../contexts/TenantContext';
import { useNotification } from '../contexts/NotificationContext';
import api from '../services/api';

interface TargetValidationProps {
  targets: string[];
  onTargetsValidated?: (allValid: boolean) => void;
  onValidationResults?: (results: ValidationResult[]) => void;
  onRequestTargets?: (targets: string[]) => Promise<void> | void;
  validateOnChange?: boolean; // If false, only validate when manually triggered or on blur
}

export interface TargetValidationRef {
  validate: () => void;
}

interface ValidationResult {
  target: string;
  is_approved: boolean;
  status?: string; // 'approved', 'requested', or undefined (not in list)
  allow_list_entry?: {
    id: string;
    value: string;
    type: string;
    status?: string;
  };
}

const TargetValidation = forwardRef<TargetValidationRef, TargetValidationProps>(({
  targets,
  onTargetsValidated,
  onValidationResults,
  onRequestTargets,
  validateOnChange = false
}, ref) => {
  const { selectedTenant } = useTenant();
  const { showNotification } = useNotification();
  const [validationResults, setValidationResults] = useState<ValidationResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState<string[]>([]);
  const [lastValidatedTargets, setLastValidatedTargets] = useState<string[]>([]);

  const validateTargets = useCallback(async (targetsToValidate: string[]) => {
    if (!selectedTenant?.tenant_id || targetsToValidate.length === 0) {
      setValidationResults([]);
      return;
    }

    setLoading(true);
    try {
      const result = await api.validateTargetsInAllowList(selectedTenant.tenant_id, targetsToValidate);
      setValidationResults(result.validation_results);
      setLastValidatedTargets(targetsToValidate);
      onTargetsValidated?.(result.all_approved);
      onValidationResults?.(result.validation_results);
    } catch (error: any) {
      console.error('Error validating targets:', error);
      showNotification('Failed to validate targets against allow list', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant?.tenant_id, onTargetsValidated, onValidationResults, showNotification]);

  // Expose validate function via ref
  useImperativeHandle(ref, () => ({
    validate: () => {
      if (targets.length > 0 && selectedTenant?.tenant_id) {
        validateTargets(targets);
      }
    }
  }));

  // Only validate when targets change if validateOnChange is true, or on initial mount
  useEffect(() => {
    if (validateOnChange) {
      // Compare current targets with last validated to avoid unnecessary calls
      const targetsStr = JSON.stringify(targets.sort());
      const lastValidatedStr = JSON.stringify(lastValidatedTargets.sort());
      
      if (targetsStr !== lastValidatedStr && targets.length > 0 && selectedTenant?.tenant_id) {
        validateTargets(targets);
      } else if (targets.length === 0) {
        setValidationResults([]);
        setLastValidatedTargets([]);
      }
    } else if (targets.length === 0) {
      setValidationResults([]);
      setLastValidatedTargets([]);
    }
  }, [targets, selectedTenant?.tenant_id, validateOnChange, lastValidatedTargets, validateTargets]);

  const handleRequestTargets = () => {
    const invalidTargets = validationResults
      .filter(result => !result.is_approved)
      .map(result => result.target);
    
    if (invalidTargets.length === 0) {
      showNotification('All targets are already approved', 'info');
      return;
    }

    setSelectedTargets(invalidTargets);
    setRequestDialogOpen(true);
  };

  const handleConfirmRequest = async () => {
    try {
      await onRequestTargets?.(selectedTargets);
      setRequestDialogOpen(false);
      showNotification('Targets have been added to your request list', 'success');
      // Revalidate targets after request
      if (targets.length > 0) {
        validateTargets(targets);
      }
    } catch (error: any) {
      console.error('Error requesting targets:', error);
      showNotification('Failed to request targets', 'error');
    }
  };

  const getStatusIcon = (result: ValidationResult) => {
    const status = result.status || (result.is_approved ? 'approved' : null);
    if (status === 'approved') {
      return <CheckIcon sx={{ color: '#4caf50' }} />; // Green
    } else if (status === 'requested') {
      return <AmberIcon sx={{ color: '#ff9800' }} />; // Amber
    } else {
      return <CancelIcon sx={{ color: '#f44336' }} />; // Red
    }
  };

  const getStatusChip = (result: ValidationResult) => {
    const status = result.status || (result.is_approved ? 'approved' : null);
    if (status === 'approved') {
      return <Chip icon={<CheckIcon />} label="Approved" color="success" size="small" />;
    } else if (status === 'requested') {
      return <Chip icon={<AmberIcon />} label="Requested" color="warning" size="small" />;
    } else {
      return <Chip icon={<CancelIcon />} label="Not Approved" color="error" size="small" />;
    }
  };

  const allApproved = validationResults.length > 0 && validationResults.every(result => result.is_approved);

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography>Validating targets against allow list...</Typography>
      </Box>
    );
  }

  if (validationResults.length === 0 && targets.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" icon={<SecurityIcon />}>
          <Typography variant="subtitle2" gutterBottom>
            Allow List Validation
          </Typography>
          <Typography variant="body2">
            Enter targets above to check if they are approved in the allow list
          </Typography>
        </Alert>
      </Box>
    );
  }

  // Always render a container to prevent layout shift, even when no validation results yet
  if (validationResults.length === 0) {
    return (
      <Box sx={{ mt: 2, minHeight: '60px' }}>
        {/* Placeholder to maintain consistent height */}
      </Box>
    );
  }

  // Determine overall status
  const hasRequested = validationResults.some(r => r.status === 'requested');
  const hasNotInList = validationResults.some(r => !r.status || (r.status !== 'approved' && r.status !== 'requested'));
  
  const getOverallSeverity = () => {
    if (allApproved) return "success";
    if (hasRequested && !hasNotInList) return "info";
    return "warning";
  };

  const getOverallMessage = () => {
    if (allApproved) return "All targets are approved in the allow list";
    if (hasRequested && !hasNotInList) return "Some targets are pending approval";
    return "Some targets are not in the allow list";
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Alert 
        severity={getOverallSeverity()} 
        icon={<SecurityIcon />}
        action={
          hasNotInList && (
            <Button
              color="inherit"
              size="small"
              onClick={handleRequestTargets}
              startIcon={<AddIcon />}
            >
              Request Targets
            </Button>
          )
        }
      >
        <Typography variant="subtitle2" gutterBottom>
          Allow List Validation
        </Typography>
        <Typography variant="body2">
          {getOverallMessage()}
        </Typography>
      </Alert>

      <List dense sx={{ mt: 1 }}>
        {validationResults.map((result, index) => {
          const status = result.status || (result.is_approved ? 'approved' : null);
          const secondaryText = status === 'approved' 
            ? `Approved via ${result.allow_list_entry?.type || 'entry'}`
            : status === 'requested'
            ? 'Pending approval'
            : 'Not in allow list';
          
          return (
            <ListItem key={index} sx={{ py: 0.5 }}>
              <ListItemIcon>
                {getStatusIcon(result)}
              </ListItemIcon>
              <ListItemText
                primary={result.target}
                secondary={secondaryText}
              />
              <Box sx={{ ml: 1 }}>
                {getStatusChip(result)}
              </Box>
            </ListItem>
          );
        })}
      </List>

      <Dialog open={requestDialogOpen} onClose={() => setRequestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon />
            Request Targets for Allow List
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The following targets are not in your tenant's allow list. You can request to add them:
          </Typography>
          <List>
            {selectedTargets.map((target, index) => (
              <ListItem key={index}>
                <ListItemText primary={target} />
              </ListItem>
            ))}
          </List>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            These targets will be added to your allow list request. A super admin will need to approve them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleConfirmRequest} variant="contained">
            Request Targets
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

TargetValidation.displayName = 'TargetValidation';

export default TargetValidation;
